/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    inboxId,
    agentId,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        inbox_id: inboxId,
        agent_id: agentId,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getDrilldown({
    metric,
    bucketTimestamp,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    page,
    perPage,
    inboxId,
    agentId,
    signal,
  }) {
    const requestConfig = {
      params: {
        metric,
        bucket_timestamp: bucketTimestamp,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        inbox_id: inboxId,
        agent_id: agentId,
        timezone_offset: getTimeOffset(),
        page,
        per_page: perPage,
      },
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    return axios.get(`${this.url}/drilldown`, requestConfig);
  }

  /* eslint-disable default-param-last */
  getSummary(
    since,
    until,
    type = 'account',
    id,
    groupBy,
    businessHours,
    extra = {}
  ) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        comparison: extra.comparison,
        comparison_since: extra.comparisonSince,
        comparison_until: extra.comparisonUntil,
        business_hours: businessHours,
        inbox_id: extra.inboxId,
        agent_id: extra.agentId,
        timezone_offset: getTimeOffset(),
      },
    });
  }
  /* eslint-enable default-param-last */

  getConversationsInsights({
    from: since,
    to: until,
    groupBy,
    businessHours,
    type = 'account',
    id,
    inboxId,
    agentId,
    comparisonSince,
    comparisonUntil,
  }) {
    return axios.get(`${this.url}/conversations_insights`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        inbox_id: inboxId,
        agent_id: agentId,
        comparison_since: comparisonSince,
        comparison_until: comparisonUntil,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours, inboxId, label }) {
    return axios.get(`${this.url}/agents`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        inbox_id: inboxId,
        label,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationsSummaryReports({
    from: since,
    to: until,
    businessHours,
    inboxId,
    agentId,
  }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        inbox_id: inboxId,
        agent_id: agentId,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationsPdf({
    from: since,
    to: until,
    groupBy,
    businessHours,
    type,
    id,
    inboxId,
    agentId,
    comparisonSince,
    comparisonUntil,
  }) {
    return axios
      .get(`${this.url}/conversations_pdf`, {
        params: {
          since,
          until,
          type,
          id,
          group_by: groupBy,
          business_hours: businessHours,
          inbox_id: inboxId,
          agent_id: agentId,
          comparison_since: comparisonSince,
          comparison_until: comparisonUntil,
          timezone_offset: getTimeOffset(),
        },
        responseType: 'blob',
      })
      .then(response => {
        const contentType = response.headers['content-type'] || '';
        if (!contentType.includes('pdf')) {
          return Promise.reject(new Error('not_pdf'));
        }
        return response.data;
      });
  }

  getAgentsPdf({ from: since, to: until, businessHours, inboxId, label }) {
    return axios
      .get(`${this.url}/agents_pdf`, {
        params: {
          since,
          until,
          business_hours: businessHours,
          inbox_id: inboxId,
          label,
          timezone_offset: getTimeOffset(),
        },
        responseType: 'blob',
      })
      .then(response => {
        const contentType = response.headers['content-type'] || '';
        if (!contentType.includes('pdf')) {
          return Promise.reject(new Error('not_pdf'));
        }
        return response.data;
      });
  }

  getLabelsPdf({ from: since, to: until, businessHours, inboxId, agentId }) {
    return axios
      .get(`${this.url}/labels_pdf`, {
        params: {
          since,
          until,
          business_hours: businessHours,
          inbox_id: inboxId,
          agent_id: agentId,
          timezone_offset: getTimeOffset(),
        },
        responseType: 'blob',
      })
      .then(response => {
        const contentType = response.headers['content-type'] || '';
        if (!contentType.includes('pdf')) {
          return Promise.reject(new Error('not_pdf'));
        }
        return response.data;
      });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getLabelReports({ from: since, to: until, businessHours, inboxId, agentId }) {
    return axios.get(`${this.url}/labels`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        inbox_id: inboxId,
        agent_id: agentId,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getAgentLabelMix({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/agent_label_mix`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
      },
    });
  }

  getLabelAgentMix({ from: since, to: until, inboxId, agentId } = {}) {
    return axios.get(`${this.url}/label_agent_mix`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        agent_id: agentId,
      },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }
}

export default new ReportsAPI();
